import React, { useState, useEffect, useMemo } from 'react';
import { Platform, ScrollView, TouchableOpacity, View } from 'react-native'

import api from "../../../../utils/api"
import useActiveAdminCommunity from '../../../../hooks/useActiveAdminCommunity'
import tw from '../../../../styles/tailwind';
import Text from '../../../../components/Text';
import IcomoonIcon from '../../../../components/IcomoonIcon';
import { Illustration } from '../../../../components/Illustration';
import { Icon } from '../../../../components/Icon';
import { useQuery } from '@tanstack/react-query';
import Header from '../../../../navigation/Header';
import IconButton from '../../../../components/IconButton';
import Toggle from '../../../../components/Toggle';
import ResidentFormModal from './ResidentFormModal';
import NewVehicleModal, { vehicleColors } from './NewVehicleModal';
import NewCommentModal from './NewCommentModal';
import AddPaymentModal from '../Fees/Fees/AddPaymentModal';
import { UnitFeeLevelModal } from '../Fees/Components/UnitFeeLevelSelector';
import AssignNewTagModal from './components/AssignNewTagModal';
import TagDetailBottomSheet from '../AccessControl/TagDetailBottomSheet';
import AssignFeeToUnitModal from './AssignFeeToUnitModal';
import SentinelAccessPanel from './components/SentinelAccessPanel';
import DebtBadge from '../../../../components/DebtBadge';


export const UnitFeesList = ({ unitFees = [], onPressFee }: Props) => {
  const [showPaid, setShowPaid] = useState(false)

  const pendingFees = useMemo(
    () => unitFees.filter(fee => !fee.is_paid),
    [unitFees]
  )

  const paidFees = useMemo(
    () => unitFees.filter(fee => fee.is_paid),
    [unitFees]
  )

  const renderFee = (unit_fee: UnitFee) => (
    <TouchableOpacity
      key={unit_fee.uuid}
      style={tw`border-b border-gray-300 pb-2 mb-2 row justify-between`}
      onPress={() => onPressFee(unit_fee)}
    >
      <View>
        <Text style={tw`text-gray-800`}>
          {unit_fee.fee.name}
        </Text>

        {unit_fee.is_paid ? (
          <Text style={tw`text-primary text-sm`}>
            ${unit_fee.paid} pagados
          </Text>
        ) : (
          <Text style={tw`text-red-600 text-sm`}>
            ${unit_fee.pending} pendientes
          </Text>
        )}
      </View>

      <IcomoonIcon
        name="chevron-right"
        size={15}
        color={tw.color('gray-500')}
      />
    </TouchableOpacity>
  )

  return (
    <View>
      {/* PENDIENTES (siempre visibles) */}
      {pendingFees.map(renderFee)}

      {/* TOGGLE */}
      {paidFees.length > 0 && (
        <TouchableOpacity
          onPress={() => setShowPaid(prev => !prev)}
          style={tw`mt-2 mb-1`}
        >
          <Text style={tw`text-gray-400 text-sm text-center`}>
            {showPaid ? 'Ocultar cuotas pagadas' : 'Mostrar cuotas pagadas'}
          </Text>
        </TouchableOpacity>
      )}

      {/* PAGADAS */}
      {showPaid && paidFees.map(renderFee)}
    </View>
  )
}



function ResidentRow({ resident, onPress } : {resident:Resident}){
  return (
    <TouchableOpacity style={tw`row justify-between bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 mb-3`} onPress={onPress}>
      <View style={tw`flex-1`}>
        <View style={tw`row`}>
          <Text style={tw`text-base`}>{resident.name}</Text>

          {
            resident.user?.is_registered &&
              <Illustration name="residentes" width={16} height={16} style={tw`ml-2`} />
          }
        </View>
        <Text style={tw`text-gray-400`}>{resident.type_str}</Text>
      </View>
    </TouchableOpacity>
  )
}


function VehicleRow({vehicle}){
  const vehicleColor = vehicleColors.find(color => color.name == vehicle.color)

  return (
    <View style={tw`bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 mb-3`}>
      <View style={tw`row`}>
        <Text style={tw``}>{vehicle.brand} {vehicle.model} {vehicle.year}</Text>
        <Text style={tw`text-gray-400 ml-3`}>{vehicle.plate_number}</Text>
      </View>

      <View style={tw`row`}>
        {
          vehicleColor &&
            <View style={tw`h-4 w-4 border-gray-200 border rounded-full bg-[${vehicleColor.hex}] mr-2`}></View>
        }
        
        <Text style={tw`text-gray-400 text-sm`}>{vehicle.color}</Text>
      </View>

    </View>
  )
}

function TagRow({ tag, isFirst, onPress, onToggleActive }){
  // Optimista: refleja el cambio al instante y revierte si la API falla.
  const [active, setActive] = useState(tag.active)

  function handleToggle(value){
    setActive(value)
    onToggleActive(tag, value).catch(() => setActive(!value))
  }

  return (
    <View style={tw.style(`row justify-between py-3`, !isFirst && `border-t border-primary/30`)}>
      <TouchableOpacity style={tw`row items-center flex-1 mr-3`} onPress={onPress}>
        <Text style={tw`text-gray-800`}>{tag.name}</Text>
        <IcomoonIcon name='chevron-right' size={12} color={tw.color('gray-400')} style={tw`ml-2`} />
      </TouchableOpacity>

      <View style={tw`row items-center`}>
        <Text style={tw.style(`mr-3`, active ? `text-primary` : `text-gray-400`)}>{active ? 'Activo' : 'Inactivo'}</Text>
        <Toggle
          value={active}
          onValueChange={handleToggle}
        />
      </View>
    </View>
  )
}




function CommentRow({ comment }) {
  return (
    <View style={tw`bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 mb-3`}>
      <Text style={tw``}>22/06/2023</Text>
      <Text style={tw`text-gray-400`}>{comment.comment}</Text>
    </View>
  )
}



export default function AdminUnitDetailScreen({ navigation, route }) {
  const { communityId, unitId } = route.params

  const isWeb = Platform.OS === 'web'

  // Toggles de la card de Accesos (opt-out por vivienda; persisten en unit.settings).
  const { community, can } = useActiveAdminCommunity()
  const sentinelOn = !!community?.sentinel_mode && community.sentinel_mode !== 'off'
  const [qrEnabled, setQrEnabled] = useState(true);
  const [proveedoresEnabled, setProveedoresEnabled] = useState(true);

  // Cada toggle se muestra solo si la comunidad tiene la feature activada a nivel global.
  const showQrToggle = !!community?.settings?.guest_passes?.resident_can_create
  const showAuthToggle = !!community?.settings?.access_authorization?.enabled

  // Ancho del área de contenido (para decidir 1 o 2 columnas en web).
  const [contentWidth, setContentWidth] = useState(0);

  const [showNewResidentModal, setShowNewResidentModal] = useState(false);
  const [showNewVehicleModal, setShowNewVehicleModal] = useState(false);
  const [showNewCommentModal, setShowNewCommentModal] = useState(false);
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [showAssignFeeModal, setShowAssignFeeModal] = useState(false);
  const [showFeeLevelSelector, setShowFeeLevelSelector] = useState(false);
  const [showAssignTagModal, setShowAssignTagModal] = useState(false);
  const [selectedResident, setSelectedResident] = useState(null);
  const [selectedTag, setSelectedTag] = useState(null);


  const { isLoading, error, data: unit, refetch:refetchUnit } = useQuery({
    queryKey: ['community.units', communityId, unitId],
    queryFn: () => api.get(`/communities/${communityId}/units/${unitId}`).then(response => response.data),
  })

  // Sincroniza los toggles con lo guardado (opt-out: ausente o true = encendido).
  useEffect(() => {
    if (!unit) return
    setQrEnabled(unit.settings?.guest_passes?.resident_can_create !== false)
    setProveedoresEnabled(unit.settings?.access_authorization?.enabled !== false)
  }, [unit])

  // Persiste un setting de la vivienda (optimista; revierte si falla).
  function persistUnitSetting(key, value, setLocal) {
    setLocal(value)
    api.patch(`/communities/${communityId}/units/${unitId}/settings`, { [key]: value })
      .then(() => refetchUnit())
      .catch(() => setLocal(!value))
  }

  // Activa/desactiva un tag (mismo endpoint del bottomsheet; sincroniza con el hardware).
  // Se manda unit_id para conservar la asignación a esta vivienda.
  function toggleTagActive(tag, value) {
    return api.put(`/communities/${communityId}/access/tags/${tag.uuid}`, { active: value, unit_id: unitId })
      .then(() => refetchUnit())
  }

  const { isLoading: loadingLevels, data: feeTypes } = useQuery({
    queryKey: ['community.fees.levels', communityId],
    queryFn: () => api.get(`/communities/${communityId}/fees/types`).then(response => response.data),
    enabled: showFeeLevelSelector,
  })

  const mainteinanceFeeTypes = useMemo(() => {
    return feeTypes?.filter(feeType => feeType.type === "main")
  }, [feeTypes])

  function goToFeeUnitScreen(feeUnit) {
    navigation.navigate("Admin.FeeUnit.Detail", {
      communityId,
      unit_fee_id: feeUnit.uuid,
      onFeeEdited: () => {
        refetchUnit()
      }
    })
  }


  // if (isLoading) return <Text style={tw`text-lg text-gray-600 mt-10 text-center`}>Cargando...</Text>

  // if (error) return <Text>Ocurrió un error: {error.message}</Text>

  // Dos columnas en web cuando hay ancho suficiente. Breakpoint bajo (480px de contenido).
  const twoColumns = isWeb && contentWidth >= 480

  const residentesSection = (
    <View style={tw`bg-white rounded-2xl border border-gray-200 py-4 px-5 mb-4`}>
      <View style={tw`row mb-3`}>
        <Illustration name="residentes" width={16} height={24} />
        <Text style={tw`text-secondary text-lg font-bold ml-3`}>Residentes</Text>
      </View>
      {
        unit?.residents?.map((resident:Resident) =>
          <ResidentRow key={resident.uuid} resident={resident} onPress={() => setSelectedResident(resident)} />
        )
      }
      {
        unit?.residents?.length === 0 &&
          <Text style={tw`text-gray-400 text-center py-4`}>Sin residentes registrados</Text>
      }
      {
        isLoading &&
          <Text style={tw`text-gray-300 text-center py-4`}>Cargando...</Text>
      }
      <IconButton title={"Registrar Residente"} style={`self-end mt-1`} onPress={() => setShowNewResidentModal(true)} />
    </View>
  )

  const cuotasSection = (
    <View style={tw.style(`bg-[#FBF5FF] border border-secondary/20 rounded-2xl py-4 px-5 mb-4`, isWeb && `pb-20`)}>
      <View style={tw`row justify-between mb-4`}>
        <View style={tw`row`}>
          <Icon name="finanzas" size={20} />
          <Text style={tw`text-secondary text-lg font-bold ml-3`}>Cuotas</Text>
        </View>
        {
          isWeb &&
            <TouchableOpacity style={tw`row items-center`} onPress={() => setShowAssignFeeModal(true)}>
              <IcomoonIcon name='add1' size={16} color={tw.color('secondary')} style={tw`mr-2`} />
              <Text style={tw`text-secondary font-600`}>Asignar nueva cuota</Text>
            </TouchableOpacity>
        }
      </View>

      <TouchableOpacity
        style={tw`row justify-between bg-white border border-gray-200 rounded-xl px-4 h-12 mb-3`}
        onPress={() => setShowFeeLevelSelector(true)}
      >
        <Text style={tw`text-gray-800`}>Tipo de cuota</Text>
        <View style={tw`row items-center`}>
          <Text style={tw`text-gray-400 mr-2 text-right`}>
            {unit?.fee_type ? `${unit.fee_type.name} ($${unit.fee_type.amount})` : 'Sin cuota'}
          </Text>
          <IcomoonIcon name='chevron-right' size={12} color={tw.color('secondary')} />
        </View>
      </TouchableOpacity>

      <TouchableOpacity
        style={tw`row justify-between bg-white border border-gray-200 rounded-xl px-4 h-12 mb-3`}
        onPress={() => navigation.navigate("Admin.Units.Fees.Balance", {communityId, unitId})}
      >
        <View style={tw`row`}>
          <Icon name="historialPagos" size={20} />
          <Text style={tw`text-secondary font-600 ml-3`}>Estado de cuenta</Text>
        </View>
        <IcomoonIcon name='chevron-right' size={16} color={tw.color('secondary')} />
      </TouchableOpacity>

      <View style={tw`bg-white border border-gray-200 rounded-xl py-3 px-3 mb-4`}>
        <UnitFeesList unitFees={unit?.unit_fees} onPressFee={goToFeeUnitScreen} />
      </View>

      {
        isWeb ?
          <View style={tw`absolute bottom-4 right-4`}>
            <IconButton title={"Registrar Pago"} style={tw`shadow-lg`} onPress={() => setShowAddPaymentModal(true)} />
          </View>
        :
          <>
            <IconButton title={"Registrar Pago"} style={`w-full mt-1`} onPress={() => setShowAddPaymentModal(true)} />
            <IconButton title={"Asignar nueva cuota"} color="secondary" textColor="white" style={`w-full mt-2`} onPress={() => setShowAssignFeeModal(true)} />
          </>
      }
    </View>
  )

  const accesosSection = (
    <View style={tw`bg-[#EAFBF9] border border-primary/30 rounded-2xl py-4 px-5 mb-4`}>
      <View style={tw`row justify-between mb-3`}>
        <View style={tw`row`}>
          <Text style={tw`text-secondary text-lg font-bold mr-2`}>Accesos</Text>
          <DebtBadge status={unit?.debt_status} />
        </View>
        {
          isWeb &&
            <TouchableOpacity style={tw`row items-center`} onPress={() => setShowAssignTagModal(true)}>
              <IcomoonIcon name='add1' size={16} color={tw.color('primary')} style={tw`mr-2`} />
              <Text style={tw`text-primary font-600`}>Asignar Tag</Text>
            </TouchableOpacity>
        }
      </View>

      <View style={tw`row mb-2`}>
        <Illustration name="tags" width={22} height={20} />
        <Text style={tw`text-secondary font-600 ml-2`}>Tags</Text>
      </View>

      <View style={tw`bg-white border border-primary/30 rounded-xl px-4 mb-3`}>
        {
          unit?.access_tags?.map((tag, i) =>
            <TagRow key={tag.uuid} tag={tag} isFirst={i === 0} onPress={() => setSelectedTag(tag)} onToggleActive={toggleTagActive} />
          )
        }
        {
          isLoading &&
            <Text style={tw`text-gray-300 text-center py-4`}>Cargando...</Text>
        }
        {
          unit?.access_tags?.length === 0 &&
            <Text style={tw`text-gray-400 text-center py-4`}>Sin tags registrados</Text>
        }
      </View>

      {
        !isWeb &&
          <IconButton title={"Asignar Tag"} style={`w-full mb-4`} onPress={() => setShowAssignTagModal(true)} />
      }

      {
        showQrToggle &&
          <>
            <View style={tw`row mb-2`}>
              <Icon name='qr' size={20} />
              <Text style={tw`text-secondary font-600 ml-2`}>Invitados QR</Text>
            </View>

            <View style={tw`row justify-between items-center bg-white border border-primary/30 rounded-xl px-4 py-3 mb-4`}>
              <Text style={tw`text-gray-500 flex-1 mr-3`}>Esta vivienda puede generar códigos de acceso QR</Text>
              <Toggle value={qrEnabled} onValueChange={v => persistUnitSetting('guest_passes.resident_can_create', v, setQrEnabled)} />
            </View>
          </>
      }

      {
        showAuthToggle &&
          <>
            <View style={tw`row mb-2`}>
              <Illustration name="autorizacion" width={20} height={20} />
              <Text style={tw`text-secondary font-600 ml-2`}>Autorizaciones</Text>
            </View>

            <View style={tw`row justify-between items-center bg-white border border-primary/30 rounded-xl px-4 py-3`}>
              <Text style={tw`text-gray-500 flex-1 mr-3`}>Esta vivienda puede solicitar acceso a proveedores</Text>
              <Toggle value={proveedoresEnabled} onValueChange={v => persistUnitSetting('access_authorization.enabled', v, setProveedoresEnabled)} />
            </View>
          </>
      }

      {
        sentinelOn && unit &&
          <View style={tw`mt-4`}>
            <SentinelAccessPanel communityId={communityId} unitId={unitId} canManage={can('access.manage')} />
          </View>
      }
    </View>
  )

  const vehiculosSection = (
    <View style={tw`bg-white rounded-2xl border border-gray-200 py-4 px-5 mb-4`}>
      <View style={tw`row mb-3`}>
        <Illustration name="vehiculos" width={36} height={20} style={tw`-ml-2`} />
        <Text style={tw`text-secondary text-lg font-bold ml-2`}>Vehículos</Text>
      </View>
      {
        unit?.vehicles?.map((vehicle: Vehicle) =>
          <VehicleRow key={vehicle.uuid} vehicle={vehicle} />
        )
      }
      {
        unit?.vehicles?.length === 0 &&
          <Text style={tw`text-gray-400 text-center py-4`}>Sin vehículos registrados</Text>
      }
      {
        isLoading &&
          <Text style={tw`text-gray-300 text-center py-4`}>Cargando...</Text>
      }
      <IconButton title={"Registrar Vehículo"} style={`self-end mt-1`} onPress={() => setShowNewVehicleModal(true)} />
    </View>
  )

  const comentariosSection = (
    <View style={tw`bg-white rounded-2xl border border-gray-200 py-4 px-5 mb-4`}>
      <View style={tw`mb-3`}>
        <View style={tw`row mb-1`}>
          <Illustration name="mensajes" width={20} height={20} />
          <Text style={tw`text-secondary text-lg font-bold ml-3`}>Comentarios</Text>
        </View>
        <Text style={tw`text-gray-400 text-xs`}>Comentarios visibles solo para administradores</Text>
      </View>
      <Text style={tw`text-gray-300 text-center py-6`}>Sin comentarios</Text>
      <IconButton title={"Comentar"} style={`self-end mt-1`} onPress={() => setShowNewCommentModal(true)} />
    </View>
  )

  return (
    <View style={tw`flex-1 bg-background`}>
      {/* Header */}
      <Header showBackButton style={`mb-0 bg-transparent`}>
        <View style={tw`row`}>
          <Text style={tw`text-xl font-bold text-secondary`}>{unit?.name ?? 'Cargando...'}</Text>
        </View>
      </Header>

      <ScrollView style={tw`flex-1`} contentContainerStyle={tw`px-3 pt-6`}>

        <View onLayout={e => setContentWidth(e.nativeEvent.layout.width)}>
          {
            twoColumns ? (
              // Web ancho: dos columnas
              <View style={tw`flex-row`}>
                <View style={tw`flex-1 mr-1.5`}>
                  {residentesSection}
                  {accesosSection}
                  {vehiculosSection}
                </View>
                <View style={tw`flex-1 ml-1.5`}>
                  {cuotasSection}
                  {comentariosSection}
                </View>
              </View>
            ) : (
              // Angosto / nativo: una sola columna
              <>
                {residentesSection}
                {cuotasSection}
                {accesosSection}
                {vehiculosSection}
                {comentariosSection}
              </>
            )
          }
        </View>

      </ScrollView>


      {/* Modals */}
      <ResidentFormModal
        resident={selectedResident}
        communityId={communityId}
        unitId={unitId}
        visible={showNewResidentModal || selectedResident}
        onRequestClose={() => {
          setShowNewResidentModal(false)
          setSelectedResident(null)
        }}
        onResidentCreated={(resident) => {
          setShowNewResidentModal(false)
          refetchUnit()
          
          setTimeout(() => {
            setSelectedResident(resident)
          }, 300);
        }}
        onResidentEdited={() => {
          setSelectedResident(null)
          refetchUnit()
        }}
        onResidentDeleted={() => {
          setSelectedResident(null)
          refetchUnit()
        }}
      />

      <NewVehicleModal
        communityId={communityId}
        unitId={unitId}
        visible={showNewVehicleModal} 
        onRequestClose={() => setShowNewVehicleModal(false)}
        onVehicleCreated={() => {
          setShowNewVehicleModal(false)
          refetchUnit()
        }}
      />

      <NewCommentModal
        communityId={communityId}
        unitId={unitId}
        visible={showNewCommentModal} 
        onRequestClose={() => setShowNewCommentModal(false)}
      />

      <AddPaymentModal
        unitId={unitId}
        communityId={communityId}
        onRequestClose={() => setShowAddPaymentModal(false)}
        visible={showAddPaymentModal}
        onPaymentCreated={() => {
          setShowAddPaymentModal(false)
          refetchUnit()
        }}
      />

      <AssignFeeToUnitModal
        visible={showAssignFeeModal}
        unit={unit}
        communityId={communityId}
        onRequestClose={() => setShowAssignFeeModal(false)}
        onFeeCreated={() => {
          setShowAssignFeeModal(false)
          refetchUnit()
        }}
      />

      {
        showFeeLevelSelector &&
          <UnitFeeLevelModal
            unit={unit}
            communityId={communityId}
            onRequestClose={() => setShowFeeLevelSelector(false)}
            feeLevels={mainteinanceFeeTypes}
            onFeeLevelSaved={() => {
              setShowFeeLevelSelector(false)
              refetchUnit()
            }}
          />
      }

      {
        showAssignTagModal &&
          <AssignNewTagModal
            communityId={communityId}
            unit={unit}
            onRequestClose={()=> setShowAssignTagModal(false)}
            onTagAssigned={()=> {
              setShowAssignTagModal(false)
              refetchUnit()
            }}
          />
      }

      {
        selectedTag &&
          <TagDetailBottomSheet
            visible={true}
            hideUnitSelector
            canDelete={false}
            communityId={communityId}
            tag={selectedTag}
            onRequestClose={() => setSelectedTag(null)}
            initialUnitId={unit.uuid}
            onTagEdited={() => {
              refetchUnit()
              setSelectedTag(null)
            }}
          />
      }
    </View>
  );
}
